<template>
  <v-container class="py-6" fluid>
    <div class="d-flex align-center mb-6">
      <v-btn icon color="primary" class="mr-3 bg-primary-light" elevation="2">
        <v-icon>mdi-account-school-outline</v-icon>
      </v-btn>
      <div>
        <h1 class="text-h5 font-weight-bold primary--text mb-0">
          Reasignación de Salón (Drag & Drop)
        </h1>
        <span class="text-caption grey--text font-weight-medium">
          Cambia de salón a los estudiantes arrastrándolos al salón deseado
        </span>
      </div>
    </div>

    <v-row>
      <!-- LEFT SECTION: STUDENTS -->
      <v-col cols="12" md="6">
        <v-card class="elevation-12 rounded-xl overflow-hidden glass-card fill-height">
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="primary">mdi-account-group</v-icon>
            <v-toolbar-title class="font-weight-bold primary--text">Estudiantes</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12" sm="6" class="pb-0">
                <v-text-field
                  v-model="searchStudent"
                  append-icon="mdi-magnify"
                  :label="$t('Buscar Estudiante')"
                  single-line
                  hide-details
                  outlined
                  dense
                  class="rounded-lg bg-grey-lighten-4 mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" class="pb-0">
                <v-autocomplete
                  v-model="filterClassroomId"
                  :items="classrooms"
                  item-text="name"
                  item-value="id"
                  label="Filtrar por Salón"
                  outlined
                  dense
                  clearable
                  hide-details
                  class="rounded-lg bg-grey-lighten-4 mb-4"
                >
                  <template v-slot:selection="data">
                    {{ data.item.grade_name }} - {{ data.item.name }}
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ data.item.grade_name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-divider class="mb-4"></v-divider>

            <div class="list-container custom-scrollbar">
              <v-progress-linear v-if="loadingStudents" indeterminate color="primary"></v-progress-linear>
              
              <v-row v-if="filteredStudents.length > 0">
                <v-col 
                  v-for="student in filteredStudents" 
                  :key="student.id" 
                  cols="12"
                  class="py-1"
                >
                  <div 
                    class="student-card d-flex align-center pa-3 rounded-lg border shadow-xs"
                    draggable="true"
                    @dragstart="onDragStart($event, student)"
                  >
                    <v-avatar size="40" class="mr-3 elevation-1">
                      <v-img :src="student.avatar || defaultAvatar"></v-img>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="font-weight-bold text-subtitle-2">{{ student.last_name }} {{ student.first_name }}</div>
                      <div class="text-caption grey--text">
                        <v-icon x-small color="grey" class="mr-1">mdi-google-classroom</v-icon>
                        {{ student.classroom || 'Sin salón asignado' }}
                      </div>
                    </div>
                    <v-icon color="grey lighten-1">mdi-drag-variant</v-icon>
                  </div>
                </v-col>
              </v-row>
              
              <div v-else-if="!loadingStudents" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-account-search-outline</v-icon>
                <p class="grey--text mt-4">No se encontraron estudiantes</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT SECTION: TARGET CLASSROOM -->
      <v-col cols="12" md="6">
        <v-card 
          class="elevation-12 rounded-xl overflow-hidden glass-card fill-height"
          :class="{'drop-zone-active': isDraggingOver}"
          @dragover.prevent="isDraggingOver = true"
          @dragleave="isDraggingOver = false"
          @drop="onDrop"
        >
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="secondary">mdi-google-classroom</v-icon>
            <v-toolbar-title class="font-weight-bold secondary--text">Salón Destino</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-autocomplete
              v-model="targetClassroomId"
              :items="classrooms"
              item-text="name"
              item-value="id"
              label="Elige un salón destino"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-google-classroom"
              class="rounded-lg bg-grey-lighten-4 mb-6"
            >
              <template v-slot:selection="data">
                {{ data.item.grade_name }} - {{ data.item.name }}
              </template>
              <template v-slot:item="data">
                <v-list-item-avatar color="secondary lighten-4">
                  <v-icon color="secondary">mdi-chair-school</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ data.item.grade_name }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-divider class="mb-4"></v-divider>

            <div 
              class="drop-area custom-scrollbar"
              :class="{'has-target': !!targetClassroomId}"
            >
              <div v-if="!targetClassroomId" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-chair-school</v-icon>
                <p class="grey--text mt-4">Selecciona un salón para reasignar estudiantes</p>
              </div>

              <div v-else>
                <div class="d-flex align-center mb-4">
                  <v-chip 
                    color="secondary" 
                    dark 
                    class="font-weight-bold"
                  >
                    <v-icon left>mdi-chair-school</v-icon>
                    {{ getSelectedClassroomName() }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <span class="text-caption font-weight-bold grey--text">
                    {{ targetClassroomStudents.length }} ESTUDIANTES EN SALÓN
                  </span>
                </div>

                <v-progress-linear v-if="loadingAssignment" indeterminate color="secondary" class="mb-4"></v-progress-linear>

                <div v-if="targetClassroomStudents.length > 0">
                  <v-row>
                    <v-col 
                      v-for="student in targetClassroomStudents" 
                      :key="student.id" 
                      cols="12"
                      class="py-1"
                    >
                      <v-hover v-slot="{ hover }">
                        <div class="enrolled-card d-flex align-center pa-3 rounded-lg border shadow-xs transition-all" :class="{'elevation-4': hover}">
                          <v-avatar size="40" class="mr-3 elevation-1">
                            <v-img :src="student.avatar || defaultAvatar"></v-img>
                          </v-avatar>
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-subtitle-2">{{ student.last_name }} {{ student.first_name }}</div>
                            <div class="text-caption grey--text">Asignado</div>
                          </div>
                        </div>
                      </v-hover>
                    </v-col>
                  </v-row>
                </div>
                
                <div v-else-if="!loadingAssignment" class="text-center py-12 drop-instruction border-dashed rounded-xl border-2">
                  <v-icon size="48" color="primary" class="animate-bounce">mdi-tray-arrow-down</v-icon>
                  <p class="primary--text font-weight-bold mt-4">Arrastra estudiantes aquí para reasignarlos</p>
                  <span class="text-caption grey--text">Solo suelta un estudiante para agregarlo a este salón</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'

export default {
  name: 'StudentClassroomDragDrop',
  data: () => ({
    searchStudent: '',
    filterClassroomId: null,
    
    loadingData: false,
    loadingStudents: false,
    loadingClassrooms: false,
    loadingAssignment: false,
    
    targetClassroomId: null,
    isDraggingOver: false,

    allStudents: [],
    classrooms: [],
    
    defaultAvatar: 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
  }),

  computed: {
    filteredStudents() {
      let filtered = this.allStudents;
      
      // Filter by text search
      if (this.searchStudent) {
        // const query = this.searchStudent.toLowerCase();
        const normalize = (str) => {
          return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : '';
        };
        
        filtered = filtered.filter(s => {
          const fullName = `${s.last_name || ''} ${s.first_name || ''}`;
          return normalize(fullName).includes(normalize(this.searchStudent));
        });
      }
      
      // Filter by classroom dropdown on the left side
      if (this.filterClassroomId) {
        filtered = filtered.filter(s => s.idClassroom === this.filterClassroomId);
      }
      
      // Exclude students that are already in the selected target classroom
      if (this.targetClassroomId) {
        filtered = filtered.filter(s => s.idClassroom !== this.targetClassroomId);
      }
      
      return filtered;
    },
    
    targetClassroomStudents() {
      if (!this.targetClassroomId) return [];
      return this.allStudents.filter(s => s.idClassroom === this.targetClassroomId);
    }
  },

  created() {
    this.init();
  },

  methods: {
    async init() {
      this.loadingData = true;
      try {
        await Promise.all([
          this.getClassrooms(),
          this.getStudents()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        this.loadingData = false;
      }
    },

    getClassrooms() {
      this.loadingClassrooms = true;
      return HTTP.get('classrooms/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.classrooms = res.data;
      }).finally(() => { this.loadingClassrooms = false; });
    },

    getStudents() {
      this.loadingStudents = true;
      return HTTP.get('student/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        const data = res.data || [];
        this.allStudents = data.sort((a, b) => {
          const nameA = (a.last_name || '').toLowerCase();
          const nameB = (b.last_name || '').toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      }).finally(() => { this.loadingStudents = false; });
    },

    getSelectedClassroomName() {
      const classroom = this.classrooms.find(c => c.id === this.targetClassroomId);
      return classroom ? `${classroom.grade_name} - ${classroom.name}` : '';
    },

    // DRAG AND DROP HANDLERS
    onDragStart(event, student) {
      event.dataTransfer.setData('studentId', student.id);
      event.dataTransfer.effectAllowed = 'copy';
    },

    onDrop(event) {
      this.isDraggingOver = false;
      const studentId = event.dataTransfer.getData('studentId');
      
      if (!studentId) return;
      if (!this.targetClassroomId) {
        this.showSnackbar('Por favor selecciona un salón destino', 'warning');
        return;
      }

      this.assignStudentToClassroom(studentId, this.targetClassroomId);
    },

    assignStudentToClassroom(idStudent, idClassroom) {
      const student = this.allStudents.find(s => s.id === idStudent);
      if (student && student.idClassroom === idClassroom) {
        this.showSnackbar('El estudiante ya está en este salón', 'warning');
        return;
      }

      this.loadingAssignment = true;
      const url = `student/${idStudent}`;
      
      HTTP.patch(url, {
        idClassroom
      }, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar('Estudiante reasignado correctamente', 'success');
        this.loadingAssignment = false;
        // Refresh students list to reflect changes including classroom name
        this.getStudents();
      }).catch(err => {
        console.error('Error assigning student:', err);
        this.showSnackbar('Error al reasignar el estudiante', 'error');
        this.loadingAssignment = false;
      });
    },

    clear() {
      this.$store.commit('snackbar_activate_false')
    },

    showSnackbar(message, color) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_activate_true')
      setTimeout(this.clear, 3000)
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.bg-primary-light {
  background-color: rgba(var(--v-primary-base), 0.08) !important;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.list-container, .drop-area {
  height: 500px;
  overflow-y: auto;
  padding: 4px;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

.student-card {
  border: 1px solid #edf2f7;
  background-color: white;
  transition: all 0.2s ease;
  cursor: grab;
}
.student-card:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.02);
  transform: translateX(4px);
}
.student-card:active {
  cursor: grabbing;
}

.enrolled-card {
  border: 1px solid #edf2f7;
  background-color: white;
}

.drop-zone-active {
  background-color: rgba(var(--v-primary-base), 0.05) !important;
  border: 2px dashed var(--v-primary-base) !important;
}

.drop-instruction {
  border-color: #e2e8f0;
  background-color: #f8fafc;
}

.border-dashed {
  border-style: dashed !important;
}

.border-2 {
  border-width: 2px !important;
}

.opacity-0 { opacity: 0; }
.opacity-100 { opacity: 1; }
.hover-scale:hover {
  transform: translateY(-2px);
}
.shadow-error {
  box-shadow: 0 4px 14px 0 rgba(255, 82, 82, 0.3) !important;
}
.border-l-4-error {
  border-left: 4px solid #ff5252;
}
.tracking-wider {
  letter-spacing: 0.5px;
}
.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}
.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.animate-bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-20px);}
  60% {transform: translateY(-10px);}
}
</style>
